import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

type GameMode = 'easy' | 'medium' | 'hard' | 'extreme';

const ChallengeMode = () => {
  const [gameMode, setGameMode] = useState<GameMode>('easy');
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [score, setScore] = useState(0);
  const [timeLimit, setTimeLimit] = useState(10);
  const [timeLeft, setTimeLeft] = useState(10);
  const [isActive, setIsActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [userAnswer, setUserAnswer] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [combo, setCombo] = useState(0);

  const gameSettings = {
    easy: { maxNum: 5, timeLimit: 15, pointsPerCorrect: 10 },
    medium: { maxNum: 10, timeLimit: 12, pointsPerCorrect: 20 },
    hard: { maxNum: 15, timeLimit: 8, pointsPerCorrect: 30 },
    extreme: { maxNum: 25, timeLimit: 5, pointsPerCorrect: 50 }
  };

  const generateQuestion = () => {
    const settings = gameSettings[gameMode];
    const difficulty = Math.min(level, 10);
    const maxNum = settings.maxNum + Math.floor(difficulty / 2);
    
    const num1 = Math.floor(Math.random() * maxNum) + 1;
    const num2 = Math.floor(Math.random() * maxNum) + 1;
    
    return {
      num1,
      num2,
      answer: num1 * num2
    };
  };

  const startGame = () => {
    setLevel(1);
    setLives(3);
    setScore(0);
    setCombo(0);
    setGameOver(false);
    setIsActive(true);
    setTimeLimit(gameSettings[gameMode].timeLimit);
    setTimeLeft(gameSettings[gameMode].timeLimit);
    setCurrentQuestion(generateQuestion());
    setUserAnswer("");
  };

  const checkAnswer = () => {
    const userNum = parseInt(userAnswer);
    const correct = userNum === currentQuestion.answer;
    
    if (correct) {
      const basePoints = gameSettings[gameMode].pointsPerCorrect;
      const comboBonus = Math.floor(combo / 5) * 10;
      const levelBonus = level * 5;
      const timeBonus = Math.max(0, timeLeft * 2);
      
      setScore(score + basePoints + comboBonus + levelBonus + timeBonus);
      setCombo(combo + 1);
      
      // Level up every 5 correct answers
      if ((combo + 1) % 5 === 0) {
        setLevel(level + 1);
        setTimeLimit(Math.max(3, timeLimit - 0.5));
      }
      
      nextQuestion();
    } else {
      setLives(lives - 1);
      setCombo(0);
      
      if (lives <= 1) {
        setGameOver(true);
        setIsActive(false);
      } else {
        nextQuestion();
      }
    }
  };

  const nextQuestion = () => {
    setCurrentQuestion(generateQuestion());
    setUserAnswer("");
    setTimeLeft(timeLimit);
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && isActive) {
      setLives(lives - 1);
      setCombo(0);
      if (lives <= 1) {
        setGameOver(true);
        setIsActive(false);
      } else {
        nextQuestion();
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeLeft, lives]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Mode Tantangan 🎮</CardTitle>
          <CardDescription>
            Tantangan perkalian dengan level yang semakin sulit!
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isActive && !gameOver ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(gameSettings).map(([mode, settings]) => {
                  const isSelected = gameMode === mode;
                  const cardClasses = `cursor-pointer transition-all hover:scale-105 ${
                    isSelected ? 'ring-2 ring-primary' : ''
                  }`;
                  
                  return (
                    <Card 
                      key={mode}
                      className={cardClasses}
                      onClick={() => setGameMode(mode as GameMode)}
                    >
                      <CardContent className="pt-4 text-center">
                        <div className="text-lg font-bold capitalize">{mode}</div>
                        <div className="text-sm text-muted-foreground">
                          Max: {settings.maxNum} × {settings.maxNum}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {settings.timeLimit}s per soal
                        </div>
                        <div className="text-sm font-semibold text-primary">
                          {settings.pointsPerCorrect} poin
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Button onClick={startGame} size="lg" className="w-full">
                Mulai Tantangan ({gameMode.toUpperCase()})
              </Button>
            </div>
          ) : isActive ? (
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-2">
                <Badge variant="default">Level {level}</Badge>
                <Badge variant="secondary">❤️ {lives}</Badge>
                <Badge variant="outline">⚡ {combo}x</Badge>
                <Badge variant={timeLeft > 5 ? "default" : "destructive"}>
                  ⏰ {timeLeft}s
                </Badge>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold">Skor: {score.toLocaleString()}</div>
                <Progress value={(timeLeft / timeLimit) * 100} className="h-3 mt-2" />
              </div>

              <Card className="bg-gradient-to-r from-primary/10 to-secondary/10">
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <div className="text-4xl font-bold">
                      {currentQuestion.num1} × {currentQuestion.num2} = ?
                    </div>
                    
                    <Input
                      type="number"
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      placeholder="Jawaban"
                      className="text-center text-xl"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && userAnswer) {
                          checkAnswer();
                        }
                      }}
                      autoFocus
                    />
                    
                    <Button 
                      onClick={checkAnswer} 
                      disabled={!userAnswer}
                      className="w-full"
                    >
                      Submit Jawaban
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {combo >= 5 && (
                <div className="text-center">
                  <Badge variant="default" className="text-lg px-4 py-2">
                    🔥 Combo {combo}x! Bonus Aktif!
                  </Badge>
                </div>
              )}
            </div>
          ) : gameOver ? (
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h3 className="text-3xl font-bold">Game Over!</h3>
                <div className="text-xl">Level Tertinggi: {level}</div>
                <div className="text-lg">Skor Akhir: {score.toLocaleString()}</div>
                <div className="text-lg">Combo Terbaik: {combo}x</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-primary">
                      {Math.floor(score / level) || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Rata-rata per Level</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-secondary">
                      {gameMode.toUpperCase()}
                    </div>
                    <div className="text-sm text-muted-foreground">Mode Terakhir</div>
                  </CardContent>
                </Card>
              </div>

              <Button onClick={startGame} className="w-full">
                Main Lagi
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChallengeMode;